<template>
    <kt-modal
        :visible="true"
        :title="recordTitle"
        :size="'modal-xl'"
        @dismiss="onClose()"
    >
        <template #body>
            <time-form v-model="record" :pm-only="props.pmOnly"></time-form>
        </template>

        <template #footer>
            <div class="kt-modal-actions">
                <button @click="onClose()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger">
                    <Trash2 :size="16" class="me-1" />Delete
                </button>
                <button @click="saveRecord()" class="btn btn-primary">
                    <Save :size="16" class="me-1" />Save
                </button>
            </div>
        </template>
    </kt-modal>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from "vue";
import ApiService from "@/services/ApiService";
import { TimeModel } from "@/models/TaskModel";
import Helper from "@/services/Helper";
import Alert from "@/services/Alert";
import TimeForm from "@/components/Time/TimeForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save } from 'lucide-vue-next';

const props = defineProps({
    recordId: {
        type: String,
        required: true
    },
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    pmOnly: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const recordTitle = ref("Time Entry");

const newRecord = (): TimeModel => {
    const rec = new TimeModel();
    // Pre-populate with requestId if provided
    if (props.requestId) {
        rec.RequestId = Number(props.requestId);
    }
    // Pre-populate with clientId if provided
    if (props.clientId) {
        rec.CustomerNumber = Number(props.clientId);
    }
    // Pre-populate with techId if provided
    if (props.techId) {
        rec.AssignedTo = Number(props.techId);
    }
    return rec;
};

const record = ref<TimeModel>(newRecord());

const saveRecord = async () => {
    try {
        let data = Helper.prep(record.value);

        console.debug("save", data);

        data = await ApiService.saveTime(data);
        
        // Close modal and refresh parent list
        emit('close');
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () => {
    Alert.notify("Deletion is currently disabled");
};

const onClose = () => {
    emit('close');
};

const loadRecord = async (val: string) => {
    switch (val) {
        case "new":
            let tmp = newRecord();
            recordTitle.value = "New Time Entry";
            record.value = tmp;
            break;

        default:
            let rec = await ApiService.loadTime(val);
            recordTitle.value = "Edit Time Entry";
            record.value = rec;
            break;
    }
};

// Load record when component mounts or recordId changes
watch(() => props.recordId, (newId) => {
    if (newId) {
        loadRecord(newId);
    }
}, { immediate: true });
</script>
